// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { CautionIcon, ErrorIcon, InfoIcon } from '@imprivata-cloud/patient-access-ui-components';
import { Flex, Typography } from 'antd';
import './ErrorComponent.less';
import { useTranslation } from 'react-i18next';
interface ErrorComponentProps {
  cause: string;
}

const feedbackIconMap = {
  error: ErrorIcon,
  info: InfoIcon,
  warning: CautionIcon,
} as const;

export const ErrorComponent: React.FC<ErrorComponentProps> = ({ cause }) => {
  const { t } = useTranslation();
  type FeedbackIconType = keyof typeof feedbackIconMap;
  // Default empty strings; translation keys resolved explicitly per case
  let title = '';
  let description = '';
  let icon: FeedbackIconType = 'error';
  switch (cause) {
    case 'patient-enrolled':
      title = t('errorComponent.youre-already-enrolled');
  description = t('errorComponent.looks-like-you-already-are-enrolled-patient-access-you-may-close-this-window');
      icon = 'warning';
      break;
    case 'minimum-age': // Age validation failed
      title = t('errorComponent.age-validation-failed');
  description = t('errorComponent.youre-not-currently-eligible-enroll-patient-access-due-minimum-age-requirement-you-may-close-this-window');
      break;
    case 'missing-info':
      title = t('errorComponent.missing-information');
  description = t('errorComponent.some-information-needed-is-missing-name-date-birth-mrn-you-may-close-this-window');
      break;
    case 'proxy-user':
      title = t('errorComponent.enrollment-not-allowed');
  description = t('errorComponent.attempt-enroll-other-record-close-window');
      icon = 'warning';
      break;
    case 'fhir-not-configured':
  title = t('errorComponent.application-is-not-configured');
  description = t('errorComponent.please-contact-hospital-administrator-assistance-you-may-close-this-window');
      icon = 'warning';
      break;
    case 'not-found':
      title = t('errorComponent.page-not-found');
  description = t('errorComponent.we-couldnt-locate-page-you-may-close-this-window');
      icon = 'warning';
      break;
    case 'session-id-not-found':
    case 'invalid-session-id':
    case 'unauthorized':
      title = t('errorComponent.session-not-found');
  description = t('errorComponent.an-error-has-occurred-current-session-is-unavailable-you-may-close-this-window');
      break;
    case 'missing-handoff-token':
      title = t('errorComponent.session-expired');
  description = t('errorComponent.your-security-this-session-has-expired-due-inactivity');
      icon = 'warning';
      break;
    case 'invalid-issuer':
  title = t('errorComponent.application-is-misconfigured');
  description = t('errorComponent.please-contact-hospital-administrator-assistance-you-may-close-this-window');
      icon = 'warning';
      break;
    case 'session-not-found':
    case 'missing-tenant-id':
      title = t('errorComponent.invalid-request');
  description = t('errorComponent.start-from-your-patient-portal-continue-you-may-close-this-window');
      icon = 'warning';
      break;
    case 'patient-deceased':
      title = t('errorComponent.unable-to-complete-enrollment');
  description = t('errorComponent.unable-proceed-enrollment-contact-hospital-close-window');
      icon = 'warning';
      break;
    case 'multiple-records-found':
      title = t('errorComponent.multiple-records-found');
      description = t('errorComponent.multiple-records-could-match-see-staff');
      icon = 'warning';
      break;
    case 'inquiry-session-mismatch':
      title = t('errorComponent.identity-verified');
  description = t('errorComponent.you-can-close-window-return-your-original-device');
      icon = 'info';
      break;
    case 'camera-access-required':
      title = t('errorComponent.camera-access-required');
  description = t('errorComponent.verify-your-identity-we-need-access-your-camera-allow-it-this-site-then-try-again');
      icon = 'warning';
      break;
    case 'feature-disabled':
      title = t('errorComponent.feature-disabled');
      description = t('errorComponent.feature-disabled-desc');
      icon = 'warning';
      break;
    case 'code-expired':
      title = t('errorComponent.code-expired');
      description = t('errorComponent.you-may-close-this-window');
      icon = 'warning';
      break;
    case 'link-expired':
      title = t('errorComponent.link-expired');
      description = t('errorComponent.link-expired-desc') + t('errorComponent.you-may-close-this-window');
      icon = 'warning';
      break;
    default:
      title = t('errorComponent.something-went-wrong');
  description = t('errorComponent.unexpected-error-occurred-you-may-close-this-window');
      break;
  }
  const bannerClassName = `error-banner error-banner--${icon}`;
  return (
    <Flex className="error-layout" gap={16} vertical data-testid="error-component">
      <div className={bannerClassName} data-testid="error-banner">
        {(() => {
          const Icon = feedbackIconMap[icon];
          return <Icon className="error-banner-icon" data-testid="error-icon" />;
        })()}
        <Typography.Title level={2} className="error-banner-title" data-testid="error-title">
          {title}
        </Typography.Title>
      </div>
      <Typography.Paragraph className="error-description" data-testid="error-text">
        {description}
      </Typography.Paragraph>
    </Flex>
  );
};
